import { useEffect, useState } from "react";
import { Rocket, Search, UserPlus, Check, Send, Zap, Users } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api } from "@/lib/api";

const SequenceConfig = ({ a }) => {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [seqId, setSeqId] = useState("");
  const [accId, setAccId] = useState("");
  const [auto, setAuto] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/apollo/sequences").then((r) => {
      setData(r.data);
      const c = r.data.config || {};
      setSeqId(c.sequence_id || "");
      setAccId(c.email_account_id || "");
      setAuto(!!c.auto_enroll);
    }).catch(() => setData({ master_key: false, sequences: [], accounts: [] }));
    api.get("/apollo/sequence-stats").then((r) => {
      if (r.data.configured) setStats(r.data);
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const seq = (data.sequences || []).find((s) => s.id === seqId);
      await api.post("/apollo/sequence-config", {
        sequence_id: seqId, sequence_name: seq?.name || "", email_account_id: accId, auto_enroll: auto,
      });
      toast.success(a.seqSaved);
    } catch (err) {
      toast.error(err.response?.data?.detail?.slice?.(0, 120) || a.failed);
    } finally {
      setSaving(false);
    }
  };

  if (!data) return null;

  return (
    <div className="border border-white/10 bg-black/30 p-4 mb-6" data-testid="apollo-sequence-config">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
        <Send className="w-3.5 h-3.5 text-[#FF7900]" /> {a.seqTitle}
      </p>
      {!data.master_key ? (
        <p className="text-xs text-white/45 leading-relaxed" data-testid="apollo-seq-no-master">{a.seqNoMaster}</p>
      ) : !data.sequences.length ? (
        <p className="text-xs text-white/45" data-testid="apollo-seq-none">{a.seqNone}</p>
      ) : (
        <>
          <p className="text-xs text-white/40 mb-3">{a.seqHint}</p>
          <div className="flex flex-wrap items-center gap-3">
            <select value={seqId} onChange={(e) => setSeqId(e.target.value)} data-testid="apollo-seq-select"
              className="flex-1 min-w-[180px] bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900]">
              <option value="">{a.seqSequence}</option>
              {data.sequences.map((s) => <option key={s.id} value={s.id}>{s.name}{s.active ? "" : " (inactive)"}</option>)}
            </select>
            <select value={accId} onChange={(e) => setAccId(e.target.value)} data-testid="apollo-seq-mailbox"
              className="flex-1 min-w-[160px] bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900]">
              <option value="">{a.seqMailbox}</option>
              {data.accounts.map((x) => <option key={x.id} value={x.id}>{x.email}</option>)}
            </select>
            <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none">
              <input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)}
                className="accent-[#FF7900]" data-testid="apollo-seq-auto-toggle" />
              {a.seqAuto}
            </label>
            <button onClick={save} disabled={saving || (auto && (!seqId || !accId))} data-testid="apollo-seq-save-btn"
              className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300">
              {saving ? "…" : a.seqSave}
            </button>
          </div>
          {stats && (
            <div className="mt-4 pt-4 border-t border-white/10" data-testid="apollo-seq-stats">
              <p className="text-xs text-white/40 mb-3">
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${stats.active ? "bg-emerald-400" : "bg-yellow-400"}`} />
                {stats.name} — {stats.active ? a.statsActive : a.statsPaused} · {stats.num_steps} {a.statsSteps}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { k: "delivered", label: a.statsDelivered, val: stats.delivered, rate: null },
                  { k: "opened", label: a.statsOpened, val: stats.opened, rate: stats.open_rate },
                  { k: "clicked", label: a.statsClicked, val: stats.clicked, rate: stats.click_rate },
                  { k: "replied", label: a.statsReplied, val: stats.replied, rate: stats.reply_rate },
                ].map((s) => (
                  <div key={s.k} className="border border-white/10 px-3 py-2" data-testid={`apollo-stat-${s.k}`}>
                    <p className="text-lg font-semibold leading-tight">{s.val}{s.rate !== null && <span className="text-xs text-[#FF7900] ml-1.5">{s.rate}%</span>}</p>
                    <p className="text-[11px] uppercase tracking-wider text-white/40">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Autopilot = ({ a }) => {
  const [cfg, setCfg] = useState(null);
  const [log, setLog] = useState([]);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("Belgium");
  const [perDay, setPerDay] = useState(10);
  const [enabled, setEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    api.get("/apollo/autopilot").then((r) => {
      const c = r.data.config || {};
      setCfg(c);
      setTitle((c.titles || []).join(", "));
      setLocation((c.locations || ["Belgium"]).join(", "));
      setPerDay(c.per_day || 10);
      setEnabled(!!c.enabled);
      setLog(r.data.log || []);
    }).catch(() => setCfg({}));
  }, []);

  const payload = () => ({
    enabled,
    titles: title.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 5),
    locations: location.split(",").map((x) => x.trim()).filter(Boolean).slice(0, 5),
    keywords: "",
    per_day: Math.min(Math.max(parseInt(perDay) || 10, 1), 25),
  });

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/apollo/autopilot", payload());
      toast.success(a.autoSaved);
    } catch (err) {
      toast.error(err.response?.data?.detail?.slice?.(0, 120) || a.failed);
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      await api.post("/apollo/autopilot", payload());
      await api.post("/apollo/autopilot/run");
      toast.success(a.autoStarted);
      setTimeout(() => {
        api.get("/apollo/autopilot").then((r) => setLog(r.data.log || [])).catch(() => {});
        setRunning(false);
      }, 45000);
    } catch (err) {
      toast.error(err.response?.data?.detail?.slice?.(0, 120) || a.failed);
      setRunning(false);
    }
  };

  if (!cfg) return null;
  const last = log[0];

  return (
    <div className="border border-white/10 bg-black/30 p-4 mb-6" data-testid="apollo-autopilot">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
        <Zap className="w-3.5 h-3.5 text-[#FF7900]" /> {a.autoTitle}
        {enabled && <span className="inline-block w-2 h-2 rounded-full bg-emerald-400" data-testid="autopilot-active-dot" />}
      </p>
      <p className="text-xs text-white/40 mb-3">{a.autoHint}</p>
      <div className="flex flex-wrap items-center gap-3">
        <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={a.titlePh}
          className="flex-1 min-w-[180px] bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
          data-testid="autopilot-titles-input" />
        <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={a.locationPh}
          className="w-40 bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
          data-testid="autopilot-location-input" />
        <label className="inline-flex items-center gap-1.5 text-xs text-white/70">
          <input type="number" min="1" max="25" value={perDay} onChange={(e) => setPerDay(e.target.value)}
            className="w-16 bg-black/40 border border-white/15 px-2 py-2 text-sm outline-none focus:border-[#FF7900]"
            data-testid="autopilot-perday-input" />
          {a.autoPerDay}
        </label>
        <label className="inline-flex items-center gap-2 text-xs text-white/70 cursor-pointer select-none">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)}
            className="accent-[#FF7900]" data-testid="autopilot-toggle" />
          {a.autoEnabled}
        </label>
        <button onClick={save} disabled={saving} data-testid="autopilot-save-btn"
          className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300">
          {saving ? "…" : a.seqSave}
        </button>
        <button onClick={runNow} disabled={running} data-testid="autopilot-run-btn"
          className="inline-flex items-center gap-1.5 border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] disabled:opacity-40 text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300">
          {running ? "…" : a.autoRun}
        </button>
      </div>
      {last && (
        <p className="text-xs text-white/45 mt-3" data-testid="autopilot-last-run">
          {a.autoLast}: {last.date} — {last.imported} {a.autoImported}, {last.emails_found} {a.autoEmails}
        </p>
      )}
    </div>
  );
};

export const ApolloPanel = () => {
  const { t } = useLang();
  const a = t.dash.apollo;
  const [configured, setConfigured] = useState(null);
  const [title, setTitle] = useState("");
  const [location, setLocation] = useState("Belgium");
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState([]);
  const [imported, setImported] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  useEffect(() => {
    api.get("/apollo/status").then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false));
  }, []);

  const search = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/apollo/search", {
        titles: title.trim() ? [title.trim()] : [],
        locations: [location.trim() || "Belgium"],
      });
      setPeople(data.people);
      if (!data.people.length) toast.info(a.noResults);
    } catch (err) {
      toast.error(err.response?.data?.detail?.slice?.(0, 120) || a.failed);
    } finally {
      setBusy(false);
    }
  };

  const importOne = async (p) => {
    try {
      const { data } = await api.post("/apollo/import", {
        apollo_id: p.apollo_id, name: p.name || "", title: p.title || "", org: p.org || "", city: p.city || "",
      });
      setImported((s) => ({ ...s, [p.apollo_id]: true }));
      toast.success(data.email_found ? a.importedEmail : a.imported);
    } catch (err) {
      if (err.response?.status === 409) setImported((s) => ({ ...s, [p.apollo_id]: true }));
      toast.error(err.response?.status === 409 ? a.already : a.failed);
    }
  };

  const importAll = async () => {
    const pending = people.filter((p) => !imported[p.apollo_id]);
    if (!pending.length) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/apollo/bulk-import", {
        people: pending.map((p) => ({ apollo_id: p.apollo_id, name: p.name || "", title: p.title || "", org: p.org || "", city: p.city || "" })),
      });
      setImported((s) => {
        const n = { ...s };
        people.forEach((p) => { n[p.apollo_id] = true; });
        return n;
      });
      toast.success(a.bulkDone.replace("{n}", data.imported).replace("{e}", data.emails_found));
    } catch (err) {
      toast.error(err.response?.data?.detail?.slice?.(0, 120) || a.failed);
    } finally {
      setBulkBusy(false);
    }
  };

  if (configured === null) return null;

  return (
    <div className="bg-[#111111] border border-white/10 p-6 md:p-8 mb-10" data-testid="apollo-panel">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/40 mb-2">
        <Rocket className="w-4 h-4 text-[#FF7900]" /> {a.title}
      </p>
      {!configured ? (
        <p className="text-xs text-white/45 leading-relaxed" data-testid="apollo-not-configured">{a.notConfigured}</p>
      ) : (
        <>
          <p className="text-xs text-white/40 mb-5">{a.hint}</p>
          <SequenceConfig a={a} />
          <Autopilot a={a} />
          <div className="flex flex-wrap gap-3 mb-5">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={a.titlePh}
              className="flex-1 min-w-[180px] bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
              data-testid="apollo-title-input" />
            <input value={location} onChange={(e) => setLocation(e.target.value)} placeholder={a.locationPh}
              className="w-44 bg-black/40 border border-white/15 px-3 py-2 text-sm outline-none focus:border-[#FF7900] transition-colors duration-300"
              data-testid="apollo-location-input" />
            <button onClick={search} disabled={busy} data-testid="apollo-search-btn"
              className="inline-flex items-center gap-2 bg-[#FF7900] hover:bg-white hover:text-black disabled:opacity-40 text-white text-sm font-semibold px-5 py-2 rounded-full transition-colors duration-300">
              <Search className="w-4 h-4" /> {busy ? "…" : a.search}
            </button>
          </div>
          <div className="space-y-2" data-testid="apollo-results">
            {people.length > 0 && people.some((p) => !imported[p.apollo_id]) && (
              <button onClick={importAll} disabled={bulkBusy} data-testid="apollo-import-all-btn"
                className="inline-flex items-center gap-2 border border-[#FF7900]/60 text-[#FF7900] hover:bg-[#FF7900] hover:text-white disabled:opacity-40 text-xs font-semibold px-4 py-2 rounded-full transition-colors duration-300 mb-2">
                <Users className="w-3.5 h-3.5" /> {bulkBusy ? "…" : a.bulkAll}
              </button>
            )}
            {people.map((p) => (
              <div key={p.apollo_id} className="flex items-center justify-between gap-3 border border-white/10 px-4 py-2.5" data-testid={`apollo-result-${p.apollo_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold truncate">{p.name}</p>
                  <p className="text-xs text-white/45 truncate">{[p.title, p.org, p.city].filter(Boolean).join(" · ")}</p>
                </div>
                <button onClick={() => importOne(p)} disabled={imported[p.apollo_id]} data-testid={`apollo-import-${p.apollo_id}`}
                  className="inline-flex items-center gap-1.5 shrink-0 text-xs font-semibold border border-white/20 hover:border-[#FF7900] hover:text-[#FF7900] disabled:opacity-40 px-3 py-1.5 rounded-full transition-colors duration-300">
                  {imported[p.apollo_id] ? <Check className="w-3.5 h-3.5" /> : <UserPlus className="w-3.5 h-3.5" />}
                  {imported[p.apollo_id] ? a.importedShort : a.import}
                </button>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};
